// Mitattu vaikutus — what the signals a recipe's ports move actually did, before and after it
// applied (ADR-033 § 1). This is the one measured thing on the Aktiivinen resepti card: two
// means of real readings over two windows of the same length, with no model between them and
// the number. Which signals appear is the knowledge base's answer (each optimization-target port
// declares `moves`, REQ-AGT-34), never a list kept here.
//
// A signal that had readings on only one side of the change is named under "ei mitattavissa" and
// never given a row — a zero delta would say the signal did not move, which is a different
// statement.
//
// Deliberately no euros. Those need metered energy (ADR-033 § 1); this shows the signals, and
// `actual_impact` beside it is the forward estimate recomputed with hindsight, not a measurement.
import type { MeasuredResponse } from "@/lib/api/building";
import { cardSubtle } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

const num = (n: number) =>
  n.toLocaleString("fi-FI", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

export function MeasuredSignals({ measured }: { measured: MeasuredResponse }) {
  if (measured.signals.length === 0 && measured.not_measured.length === 0) {
    return null;
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
        Mitattu vaikutus · {measured.window_hours} h ennen ja jälkeen
      </div>
      <div className="space-y-1">
        {measured.signals.map((signal) => (
          <div
            key={signal.data_point_id}
            title={`${signal.samples_before} lukemaa ennen · ${signal.samples_after} jälkeen`}
            className={cn(
              cardSubtle,
              "flex items-center gap-3 px-2.5 py-1.5 text-xs",
            )}
          >
            <span className="flex-1 truncate">{signal.data_point_id}</span>
            <span className="text-muted-foreground shrink-0 tabular-nums">
              {num(signal.before_mean)} → {num(signal.after_mean)}
              {signal.unit ? ` ${signal.unit}` : ""}
            </span>
            <span
              className={cn(
                "font-medium tabular-nums shrink-0 w-16 text-right",
                signal.delta === 0 ? "text-muted-foreground" : "text-success",
              )}
            >
              {signal.delta > 0 ? "+" : ""}
              {num(signal.delta)}
            </span>
          </div>
        ))}
      </div>
      {measured.not_measured.length > 0 && (
        <div className="mt-1.5 text-[11px] text-muted-foreground">
          Ei mitattavissa: {measured.not_measured.join(" · ")} — lukemia vain
          muutoksen toiselta puolelta.
        </div>
      )}
    </div>
  );
}
